import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, XCircle, Tag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { LAUNCH_PROMO_CODES, isPromoActive, PH_PROMO_CODE, isPHPromoActive } from "@/components/landing/UrgencyBanner";

interface CheckoutConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseType: "subscription" | "gift" | "boost";
  tier: string;
  duration?: string;
  price: number;
  currency?: string;
  onConfirm: (couponCode?: string) => Promise<void>;
  giftDetails?: {
    recipientName: string;
    recipientEmail: string;
    message: string;
  };
}

export function CheckoutConfirmationModal({
  open,
  onOpenChange,
  purchaseType,
  tier,
  duration,
  price,
  currency = "€",
  onConfirm,
  giftDetails
}: CheckoutConfirmationModalProps) {
  const [couponCode, setCouponCode] = useState("");
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [validationStatus, setValidationStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [isProcessing, setIsProcessing] = useState(false);
  const [validatedStripeCouponId, setValidatedStripeCouponId] = useState<string | null>(null);
  const { toast } = useToast();

  // Auto-show promo input if coming from promo link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('promo') === 'launch50' && isPromoActive()) {
      setShowCouponInput(true);
    }
  }, []);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!open) {
      setCouponCode("");
      setShowCouponInput(false);
      setValidationStatus('idle');
      setValidatedStripeCouponId(null);
    }
  }, [open]);

  // Validate coupon code
  useEffect(() => {
    if (!couponCode || !showCouponInput) {
      setValidationStatus('idle');
      setValidatedStripeCouponId(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      setValidationStatus('checking');
      
      const upperCode = couponCode.toUpperCase();
      
      // Product Hunt promo code - valid for Premium Economy monthly only
      if (isPHPromoActive() && purchaseType === 'subscription' && duration === 'Monthly') {
        if (upperCode === PH_PROMO_CODE && tier === 'premium_economy') {
          setValidationStatus('valid');
          setValidatedStripeCouponId(null); // Handled server-side with trial
          return;
        }
        // Show invalid if using ODYVOYPH for wrong tier
        if (upperCode === PH_PROMO_CODE && tier !== 'premium_economy') {
          setValidationStatus('invalid');
          setValidatedStripeCouponId(null);
          return;
        }
      }
      
      // Launch promo codes - only valid if promo is active and for monthly subscriptions
      if (isPromoActive() && purchaseType === 'subscription' && duration === 'Monthly') {
        if (upperCode === 'LAUNCH50PE' && tier === 'premium_economy') {
          setValidationStatus('valid');
          setValidatedStripeCouponId(LAUNCH_PROMO_CODES.stripe_ids.premium_economy);
          return;
        }
        if (upperCode === 'LAUNCH50FC' && tier === 'first_class') {
          setValidationStatus('valid');
          setValidatedStripeCouponId(LAUNCH_PROMO_CODES.stripe_ids.first_class);
          return;
        }
        // Allow cross-tier codes with appropriate message
        if (upperCode === 'LAUNCH50PE' || upperCode === 'LAUNCH50FC') {
          setValidationStatus('invalid');
          setValidatedStripeCouponId(null);
          return;
        }
      }
      
      // Win-back coupon pattern: ODYVOY-TRAVEL-AGAIN-XX
      const winBackPattern = /^ODYVOY-TRAVEL-AGAIN-\d{2}$/;
      
      // Referral code pattern: alphanumeric, 6-20 characters
      const referralPattern = /^[A-Za-z0-9]{6,20}$/;
      
      if (winBackPattern.test(upperCode) || referralPattern.test(upperCode)) {
        setValidationStatus('valid');
        setValidatedStripeCouponId(null); // These use different discount logic
      } else {
        setValidationStatus('invalid');
        setValidatedStripeCouponId(null);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [couponCode, showCouponInput, tier, purchaseType, duration]);

  const handleConfirm = async () => {
    if (validationStatus === 'invalid') {
      toast({
        title: "Invalid promo code",
        description: "Please enter a valid promo code or proceed without one.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    // Track checkout started in GA4
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'checkout_started', {
        purchase_type: purchaseType,
        tier: tier,
        duration: duration,
        price: price,
        has_promo: validationStatus === 'valid' && !!couponCode,
        promo_code: validationStatus === 'valid' ? couponCode : undefined
      });
      
      // Track promo claimed if valid promo code is used
      if (validationStatus === 'valid' && couponCode) {
        window.gtag('event', 'promo_claimed', {
          promo_code: couponCode,
          tier: tier,
          discount_amount: getDiscountAmount()
        });
      }
    }
    
    try {
      // Pass Stripe coupon ID if launch promo, otherwise pass the code for referral/winback logic
      const codeToPass = validatedStripeCouponId || (showCouponInput && validationStatus === 'valid' ? couponCode : undefined);
      await onConfirm(codeToPass);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getPurchaseTitle = () => {
    switch (purchaseType) {
      case "subscription":
        return `${tier.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Subscription`;
      case "gift":
        return `Gift ${tier.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`;
      case "boost":
        return `${tier.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Boost Pass`;
      default:
        return "Purchase";
    }
  };

  const getDiscountAmount = () => {
    if (!showCouponInput || validationStatus !== 'valid' || !couponCode) return 0;
    
    const upperCode = couponCode.toUpperCase();
    
    // Product Hunt promo: 50% off first month
    if (upperCode === PH_PROMO_CODE && tier === 'premium_economy') {
      return price * 0.5; // Display the 50% discount
    }
    
    // Launch promo: 50% off
    if (validatedStripeCouponId) {
      return price * 0.5;
    }
    
    // Win-back coupons: extract percentage from code
    const winBackMatch = upperCode.match(/ODYVOY-TRAVEL-AGAIN-(\d{2})$/);
    if (winBackMatch) {
      return price * (parseInt(winBackMatch[1]) / 100);
    }
    
    // Referral codes: 10% discount
    if (/^[A-Za-z0-9]{6,20}$/.test(upperCode)) {
      return price * 0.1;
    }
    
    return 0;
  };
  
  // Check if PH promo is being used
  const isPHPromoApplied = showCouponInput && validationStatus === 'valid' && couponCode.toUpperCase() === PH_PROMO_CODE && tier === 'premium_economy';

  const discount = getDiscountAmount();
  const finalPrice = price - discount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Confirm Your Purchase</DialogTitle>
          <DialogDescription>
            Review your order details before proceeding to payment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Purchase Summary */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground">Purchase Details</h3>
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm">Plan</span>
                <span className="font-medium">{getPurchaseTitle()}</span>
              </div>
              {duration && (
                <div className="flex justify-between">
                  <span className="text-sm">Duration</span>
                  <span className="font-medium">{duration}</span>
                </div>
              )}
              {giftDetails && (
                <>
                  <div className="flex justify-between">
                    <span className="text-sm">Recipient</span>
                    <span className="font-medium">{giftDetails.recipientName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Email</span>
                    <span className="font-medium text-xs">{giftDetails.recipientEmail}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Promo Code Section */}
          <div className="space-y-3">
            {!showCouponInput ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCouponInput(true)}
                className="text-primary hover:text-primary/80"
              >
                <Tag className="w-4 h-4 mr-2" />
                Have a promo code?
              </Button>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="coupon-code">Promo Code</Label>
                <div className="relative">
                  <Input
                    id="coupon-code"
                    placeholder="Enter promo code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    className="pr-10"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {validationStatus === 'checking' && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {validationStatus === 'valid' && (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                    {validationStatus === 'invalid' && (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                </div>
                {validationStatus === 'valid' && (
                  <p className="text-xs text-green-600">Valid promo code! Discount will be applied.</p>
                )}
                {validationStatus === 'invalid' && (
                  <p className="text-xs text-destructive">Invalid promo code format.</p>
                )}
              </div>
            )}
          </div>

          {/* Product Hunt Promo Info */}
          {isPHPromoApplied && (
            <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-semibold">
                <CheckCircle2 className="h-4 w-4" />
                Product Hunt Special Applied!
              </div>
              <ul className="text-sm text-green-600 dark:text-green-500 space-y-1 pl-6 list-disc">
                <li><strong>€3.49</strong> first month (50% off)</li>
                <li>Then €6.99/month • Cancel anytime</li>
              </ul>
            </div>
          )}

          {/* Price Breakdown */}
          <div className="space-y-2 pt-4 border-t">
            {isPHPromoApplied ? (
              <>
                <div className="flex justify-between text-sm">
                  <span>First month</span>
                  <span className="font-medium">{currency}3.49 <span className="text-muted-foreground line-through text-xs">€6.99</span></span>
                </div>
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Then monthly</span>
                  <span>{currency}6.99/month</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Due today</span>
                  <span className="text-green-600">{currency}3.49</span>
                </div>
              </>

            ) : (
              <>
                <div className="flex justify-between text-sm">
                  <span>Original Price</span>
                  <span>{currency}{price.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-{currency}{discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total</span>
                  <span>{currency}{finalPrice.toFixed(2)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isProcessing || validationStatus === 'invalid'}
            className="flex-1"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              "Confirm & Pay"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
